import React, { useMemo, useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { formatDuracionMin } from "../../utils/tiempos";
import { getStoredUser } from "../../services/api";
import {
  getKPIs,
  exportarInforme,
  parseFilenameFromHeader,
  downloadBlob,
} from "../../services/informes";
import useFiltrosInformes from "../../hooks/useFiltrosInformes";

function diffDays(desde, hasta) {
  if (!desde || !hasta) return 0;
  const [y1, m1, d1] = desde.split("-").map(Number);
  const [y2, m2, d2] = hasta.split("-").map(Number);
  const a = Date.UTC(y1, m1 - 1, d1);
  const b = Date.UTC(y2, m2 - 1, d2);
  return Math.floor((b - a) / 86400000);
}

function labelFromUser(u) {
  if (!u) return "";
  const parts = [u.nombre, u.apellido1, u.apellido2].filter(Boolean);
  const name = parts.join(" ").trim();
  return name || u.email || u.usuario_email || `Usuario ${u.id}`;
}

function slugify(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function buildFilename(tipoInforme, filtros, formato, empresa, usuarioLabel) {
  const ini = filtros?.fechaDesde || "";
  const fin = filtros?.fechaHasta || "";
  const ext = formato === "generate" ? "pdf" : formato;
  const empresaSlug = slugify(empresa) || "empresa";
  const usuarioSlug = usuarioLabel ? slugify(usuarioLabel) : "";
  const userSuffix = usuarioSlug ? `_${usuarioSlug}` : "";
  return `informes_${tipoInforme}_${empresaSlug}_${ini}_${fin}${userSuffix}.${ext}`;
}

function formatBalance(mins = 0) {
  const val = Number(mins) || 0;
  const sign = val > 0 ? "+" : val < 0 ? "-" : "";
  return `${sign}${formatDuracionMin(Math.abs(val))}`;
}

function StatCard({ label, value, tone = "neutral", loading = false, hint }) {
  const styles = {
    primary: "text-slate-900 bg-white/80 border-slate-200",
    success: "text-slate-900 bg-white/80 border-slate-200",
    warning: "text-slate-900 bg-white/80 border-slate-200",
    neutral: "text-slate-900 bg-white/80 border-slate-200",
  };
  const isSimpleValue = typeof value === "string" || typeof value === "number";

  return (
    <div className={`rounded-2xl border px-4 py-3 shadow-sm ${styles[tone] || styles.neutral}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 ${isSimpleValue ? "text-xl font-semibold" : ""}`}>
        {loading ? <span className="inline-block h-5 w-24 animate-pulse rounded bg-slate-200" /> : value}
      </div>
      {hint && !loading && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function ActionButton({ label, variant = "primary", onClick, disabled = false, loading = false }) {
  const cls =
    variant === "primary"
      ? "bg-blue-600 text-white hover:bg-blue-700"
      : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300";

  return (
    <button
      className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${cls} ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      {loading ? "Procesando..." : label}
    </button>
  );
}

function ReportCard({ title, description, actions = [] }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col">
      <div className="text-sm text-slate-500 uppercase tracking-wide">Informe</div>
      <h3 className="mt-1 text-lg font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 flex-1">{description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {actions.map((a, idx) => (
          <ActionButton
            key={`${title}-${idx}`}
            label={a.label}
            variant={a.variant}
            onClick={a.onClick}
            disabled={a.disabled}
            loading={a.loading}
          />
        ))}
      </div>
    </div>
  );
}

export default function InformesPanel({ token, usuarios = [] }) {
  const {
    presets,
    filters,
    setPreset,
    setDesde,
    setHasta,
    setUsuarioIds,
    buildPayload,
  } = useFiltrosInformes();

  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [filterError, setFilterError] = useState("");
  const [exporting, setExporting] = useState({});
  const [usuariosOpen, setUsuariosOpen] = useState(false);
  const usuariosRef = useRef(null);
  const storedUser = getStoredUser() || {};
  const storedCompany = storedUser.company || {};
  const pausaMin =
    Number(storedCompany.pausa_comida_minutos ?? storedUser.pausa_comida_minutos ?? 0) || 0;
  const descuentaRaw =
    storedCompany.descuenta_pausa_en_resumen ?? storedUser.descuenta_pausa_en_resumen;
  const descuentaPausa =
    descuentaRaw == null
      ? true
      : typeof descuentaRaw === "string"
        ? descuentaRaw.toLowerCase() !== "false"
        : Boolean(descuentaRaw);
  const showEfectivas = pausaMin > 0 && descuentaPausa;

  const usuariosList = useMemo(() => {
    const list = Array.isArray(usuarios) ? usuarios : [];
    return list.map((u) => ({ id: String(u.id), nombre: labelFromUser(u), raw: u }));
  }, [usuarios]);

  const allUserIds = useMemo(() => usuariosList.map((u) => u.id), [usuariosList]);
  const selectedIds = useMemo(() => {
    if (!Array.isArray(filters.usuarioIds) || filters.usuarioIds.length === 0) {
      return allUserIds;
    }
    return filters.usuarioIds
      .map((id) => String(id))
      .filter((id) => allUserIds.includes(id));
  }, [filters.usuarioIds, allUserIds]);

  const isAllSelected = selectedIds.length === allUserIds.length && allUserIds.length > 0;

  const empleadoLabel = useMemo(() => {
    if (isAllSelected) return "Todos";
    if (selectedIds.length === 1) {
      const found = usuariosList.find((u) => u.id === String(selectedIds[0]));
      return found ? found.nombre : "Usuario";
    }
    return selectedIds.length ? `${selectedIds.length} usuarios` : "Usuarios";
  }, [isAllSelected, selectedIds, usuariosList]);

  useEffect(() => {
    if (!Array.isArray(filters.usuarioIds) || filters.usuarioIds.length === 0) return;
    const cleaned = filters.usuarioIds
      .map((id) => String(id))
      .filter((id) => allUserIds.includes(id));
    if (cleaned.length === allUserIds.length) {
      setUsuarioIds([]);
      return;
    }
    if (cleaned.length !== filters.usuarioIds.length) {
      setUsuarioIds(cleaned);
    }
  }, [filters.usuarioIds, allUserIds, setUsuarioIds]);

  useEffect(() => {
    if (!usuariosOpen) return;
    const handler = (e) => {
      if (!usuariosRef.current || usuariosRef.current.contains(e.target)) return;
      setUsuariosOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [usuariosOpen]);

  const validateFilters = (f) => {
    if (!f.desde || !f.hasta) {
      return { ok: false, msg: "Selecciona fecha desde y hasta" };
    }
    if (f.desde > f.hasta) {
      return { ok: false, msg: "Rango de fechas inválido" };
    }
    const rangeDays = diffDays(f.desde, f.hasta);
    if (rangeDays > 366) {
      return { ok: false, msg: "El rango máximo es de 366 días" };
    }
    return { ok: true, msg: "" };
  };

  useEffect(() => {
    const check = validateFilters(filters);
    setFilterError(check.ok ? "" : check.msg);
    if (!check.ok || !token) {
      setResumen(null);
      setLoadingResumen(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    const payload = buildPayload();
    const timer = setTimeout(async () => {
      setLoadingResumen(true);
      try {
        const data = await getKPIs(payload, token, controller.signal);
        if (!active) return;
        setResumen(data || null);
      } catch (err) {
        if (!active || err?.name === "AbortError") return;
        console.error(err);
        setResumen(null);
        toast.error(err?.message || "Error al cargar KPIs");
      } finally {
        if (active) setLoadingResumen(false);
      }
    }, 180);

    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [filters, token, buildPayload]);

  const resumenCards = useMemo(() => {
    const hasData = !!resumen;
    const horasTotales = hasData ? formatDuracionMin(resumen.jornada_min || 0) : "--";
    const horasEfectivas = hasData ? formatDuracionMin(resumen.efectivas_min || 0) : "--";
    const diasTrabajados = hasData ? String(resumen.dias_trabajados || 0) : "--";
    const festivos = hasData ? Number(resumen.festivos || 0) : 0;
    const ausenciasBase = hasData ? Number(resumen.ausencias_dias ?? resumen.ausencias ?? 0) : 0;
    const ausenciasDias = hasData ? String(ausenciasBase) : "--";
    const festivosDias = hasData ? String(festivos) : "--";
    const balanceRaw = hasData && resumen?.balance_min != null ? Number(resumen.balance_min) : null;
    const diasTrabajadosNum = hasData ? Number(resumen.dias_trabajados || 0) : 0;
    const balancePorDiaRaw =
      balanceRaw == null || diasTrabajadosNum <= 0
        ? null
        : Math.round(balanceRaw / diasTrabajadosNum);
    const balance = balancePorDiaRaw == null ? "--" : formatBalance(balancePorDiaRaw);
    const totalUsuarios = allUserIds.length;
    const selectedCount = isAllSelected ? totalUsuarios : selectedIds.length;
    const empleadosIncluidos = String(Math.max(0, selectedCount));
    const balanceTone = balancePorDiaRaw == null
      ? "neutral"
      : balancePorDiaRaw > 0
        ? "success"
        : balancePorDiaRaw < 0
          ? "warning"
          : "neutral";

    const ausenciasFestivosValue = (
      <div className="flex items-center gap-5">
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-slate-900">{ausenciasDias}</span>
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Ausencias</span>
        </div>
        <span className="h-8 w-px bg-slate-200" />
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-slate-900">{festivosDias}</span>
          <span className="text-[10px] uppercase tracking-wide text-slate-500">Festivos</span>
        </div>
      </div>
    );

    return [
      { label: "Horas totales registradas", value: horasTotales },
      showEfectivas ? { label: "Horas efectivas", value: horasEfectivas } : null,
      { label: "Días trabajados", value: diasTrabajados },
      { label: "Ausencias y festivos", value: ausenciasFestivosValue },
      { label: "Balance medio/día", value: balance, tone: balanceTone },
      {
        label: "Usuarios incluidos",
        value: empleadosIncluidos,
        hint: isAllSelected ? "Todos" : empleadoLabel,
      },
    ].filter(Boolean);
  }, [resumen, allUserIds.length, isAllSelected, selectedIds.length, empleadoLabel, showEfectivas]);

  const runExport = async (kind, formato) => {
    const check = validateFilters(filters);
    if (!check.ok) {
      toast.error(check.msg);
      return;
    }
    if (!token) {
      toast.error("Sesión inválida");
      return;
    }

    const filtrosPayload = buildPayload();
    const tipoMap = {
      registro: "registro_legal",
      rrhh: "rrhh",
      auditoria: "ausencias",
    };
    const tipoInforme = tipoMap[kind] || kind;
    const key = `${kind}-${formato}`;
    setExporting((prev) => ({ ...prev, [key]: true }));

    try {
      const res = await exportarInforme(tipoInforme, formato, filtrosPayload, token);
      const headerName = parseFilenameFromHeader(res);
      const stored = getStoredUser() || {};
      const empresa =
        stored.company_name ||
        stored.company?.name ||
        stored.companyName ||
        stored.empresa ||
        "empresa";
      let usuarioLabel = null;
      if (filtrosPayload.usuarioId) {
        const foundUser = usuariosList.find((u) => u.id === String(filtrosPayload.usuarioId));
        usuarioLabel = foundUser?.raw?.email || foundUser?.nombre || empleadoLabel;
      }
      const filename = headerName || buildFilename(tipoInforme, filtrosPayload, formato, empresa, usuarioLabel);
      await downloadBlob(res, filename);
      toast.success("Informe exportado");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "No se pudo exportar el informe");
    } finally {
      setExporting((prev) => ({ ...prev, [key]: false }));
    }
  };

  const usuarioSummary =
    allUserIds.length === 0 ? "Sin usuarios" : empleadoLabel;

  const toggleAllUsers = () => {
    setUsuarioIds([]);
  };

  const toggleUser = (id) => {
    if (!allUserIds.length) return;
    setUsuarioIds((prev) => {
      const prevIds = Array.isArray(prev) ? prev.map(String) : [];
      const isAll = prevIds.length === 0;
      const base = isAll ? allUserIds : prevIds;
      const nextSet = new Set(base);
      if (nextSet.has(id)) {
        if (nextSet.size === 1) return base;
        nextSet.delete(id);
      } else {
        nextSet.add(id);
      }
      const next = Array.from(nextSet);
      if (next.length === allUserIds.length) {
        return [];
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-blue-900">Informes y reportes</h2>
        <p className="mt-1 text-sm sm:text-base text-slate-600">
          Centro de generación de informes legales y de RRHH
        </p>
      </header>

      <section className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Filtros globales
            </span>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => {
                const active = filters.preset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPreset(p.key)}
                    className={[
                      "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                      active
                        ? "bg-[#004B87]/10 text-[#004B87]"
                        : "bg-white text-slate-600 border border-slate-200 hover:bg-[#004B87]/5",
                    ].join(" ")}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              Desde
              <input
                type="date"
                value={filters.desde}
                onChange={(e) => setDesde(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004B87]/20"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              Hasta
              <input
                type="date"
                value={filters.hasta}
                onChange={(e) => setHasta(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004B87]/20"
              />
            </label>
            <div className="relative" ref={usuariosRef}>
              <div className="text-xs text-slate-500">Usuario</div>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={usuariosOpen}
                onClick={() => setUsuariosOpen((prev) => !prev)}
                className="mt-1 flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004B87]/20"
              >
                <span className="truncate">{usuarioSummary}</span>
                <svg
                  className={`h-4 w-4 text-slate-500 transition ${usuariosOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {usuariosOpen && (
                <div className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
                  <div className="max-h-60 overflow-auto p-2">
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#004B87]"
                        checked={isAllSelected}
                        onChange={toggleAllUsers}
                      />
                      <span>Todos</span>
                    </label>
                    <div className="my-1 h-px bg-slate-100" />
                    {usuariosList.length === 0 && (
                      <div className="px-2 py-2 text-xs text-slate-500">Sin usuarios disponibles</div>
                    )}
                    {usuariosList.map((u) => (
                      <label
                        key={u.id}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#004B87]"
                          checked={selectedIds.includes(u.id)}
                          onChange={() => toggleUser(u.id)}
                        />
                        <span className="truncate">{u.nombre}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {filterError && (
            <div className="mt-2 text-xs text-rose-600">{filterError}</div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {resumenCards.map((card, idx) => (
            <StatCard
              key={`${card.label}-${idx}`}
              label={card.label}
              value={card.value}
              hint={card.hint}
              tone={card.tone}
              loading={loadingResumen}
            />
          ))}
        </div>
      </section>


      

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">Generador de informes</h3>
          <span className="text-xs text-slate-500">Exportaciones seguras por empresa</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ReportCard
            title="Registro horario legal"
            description="Exportación oficial diaria para cumplimiento normativo."
            actions={[
              {
                label: "PDF",
                variant: "primary",
                loading: exporting["registro-pdf"],
                disabled: exporting["registro-pdf"],
                onClick: () => runExport("registro", "pdf"),
              },
              {
                label: "Excel",
                variant: "outline",
                loading: exporting["registro-xlsx"],
                disabled: exporting["registro-xlsx"],
                onClick: () => runExport("registro", "xlsx"),
              },
            ]}
          />
          <ReportCard
            title="Informe RRHH consolidado"
            description="Resumen de actividad por persona con indicadores clave."
            actions={[
              {
                label: "Generar",
                variant: "primary",
                loading: exporting["rrhh-generate"],
                disabled: exporting["rrhh-generate"],
                onClick: () => runExport("rrhh", "generate"),
              },
              {
                label: "Excel",
                variant: "outline",
                loading: exporting["rrhh-xlsx"],
                disabled: exporting["rrhh-xlsx"],
                onClick: () => runExport("rrhh", "xlsx"),
              },
            ]}
          />
          <ReportCard
            title="Informe de ausencias y festivos"
            description="Registro diario con jornadas y días especiales (festivos, permisos, vacaciones)."
            actions={[
              {
                label: "PDF",
                variant: "primary",
                loading: exporting["auditoria-pdf"],
                disabled: exporting["auditoria-pdf"],
                onClick: () => runExport("auditoria", "pdf"),
              },
              {
                label: "Excel",
                variant: "outline",
                loading: exporting["auditoria-xlsx"],
                disabled: exporting["auditoria-xlsx"],
                onClick: () => runExport("auditoria", "xlsx"),
              },
            ]}
          />
        </div>
      </section>

      
    </div>
  );
}
